// In-app guide for the offers list ("/"). Pure data — see help/types.ts.
// Change this page (or a rule a step quotes) → update this file in the same commit.
import type { PageHelp } from "../../help/types";

export const offersHelp: PageHelp = {
  title: "Offers",
  intro:
    "Every MTPL and CASCO offer, newest first. Start a new offer here, find an existing one, follow its status from offer to invoice to issued policy, and act on several offers at once (payments, policies, group invoices). Rows load 40 at a time as you scroll.",
  sections: [
    "Getting around",
    "Start an offer",
    "Find an offer",
    "Read the list",
    "Act on one offer",
    "Act on several offers",
    "Export and bulk import",
  ],
  contexts: {
    "new-menu": 'Click "New Offer" at the top left.',
    "row-menu": "Click ⋮ next to an Offer ID.",
    "selection": "Tick the checkbox of at least one row.",
    "export-dialog": 'Click the Excel or PDF export icon at the top right.',
    "export-invoices-dialog": 'Click the invoice export icon at the top right.',
    "group-invoice": 'Select two or more rows in status Invoice, click "Create group invoice", then "Continue".',
    "invalidate-dialog": 'Open ⋮ on an offer without a chosen insurer and click "Invalidate Offer".',
    "mark-as-error-dialog": 'Open ⋮ on an offer and click "Mark as Error" (administrators only).',
    "error-entries": 'Choose "Error entries" or "All entries" in the drop-down next to the search box (administrators and read-only administrators).',
    "bulk-prefill": 'Click "Bulk Prefill".',
  },
  // the guide opens these itself; "group-invoice" needs the reader's choice of offers
  reveal: {
    "new-menu": ['[data-help="offers-new-menu"]'],
    "row-menu": ['[data-help="offers-row-menu"]'],
    "selection": ['[data-help="offers-table"] .ms-DetailsRow [data-selection-toggle="true"]'],
    "export-dialog": ['[data-help="offers-export-excel"]'],
    "export-invoices-dialog": ['[data-help="offers-export-invoices"]'],
    "invalidate-dialog": ['[data-help="offers-row-menu"]', '[data-help="offers-row-menu-invalidate"]'],
    "mark-as-error-dialog": ['[data-help="offers-row-menu"]', '[data-help="offers-row-menu-mark-as-error"]'],
    "bulk-prefill": ['[data-help="offers-bulk-prefill"]'],
  },
  tips: [
    "The status column is the offer's life: Offer → Offer Saved / Sent → Invoice → Invoice Sent → Invoice Paid → Policy Issued. Grouped invoices have their own Group Invoice statuses.",
    "Clicking a row only selects it. To open an offer, click its Offer ID.",
    'Search and the MTPL / Casco filter work together with the filters in "Column options"; the result count at the right always shows how many offers match.',
  ],
  steps: [
    // --- Getting around ----------------------------------------------------------
    {
      selector: '[data-help="app-help-launcher"]',
      title: "The page guide",
      body: 'This button, in the top bar on every page that has a guide. It opens the manual for the page you are on — this panel — with "Show me around" for a tour of what is on screen, "Point at a field" to click a control and read what it does, and "Ask" for questions about any screen.',
      section: "Getting around",
      i18n: { lv: { title: "Lapas ceļvedis", body: "Šī poga augšējā joslā ir katrā lapā, kurai ir ceļvedis. Tā atver pašreizējās lapas rokasgrāmatu — šo paneli — ar “Ekskursija pa lapu”, lai apskatītu ekrānā redzamo, “Norādīt lauku”, lai noklikšķinātu uz vadīklas un izlasītu, ko tā dara, un “Jautāt”, lai uzdotu jautājumus par jebkuru ekrānu." } },
    },
    {
      selector: '[data-help="app-notifications"]',
      title: "Notifications",
      body: "Messages that popped up during this session — mostly errors returned by the server — are collected here, and the number counts them. Open the panel to read one again or to clear them; the list starts empty after a reload.",
      section: "Getting around",
      i18n: { lv: { title: "Paziņojumi", body: "Šeit tiek apkopoti šīs sesijas laikā parādītie ziņojumi — galvenokārt servera atgrieztās kļūdas —, un skaitlis rāda to skaitu. Atveriet paneli, lai ziņojumu izlasītu vēlreiz vai tos notīrītu; pēc lapas pārlādes saraksts ir tukšs." } },
    },
    {
      selector: '[data-help="app-user-manual"]',
      title: "Download the user manual",
      body: "Downloads the complete QTG user manual as a PDF, with screenshots of every page. You get it in the language selected in Settings: Latvian when the app is in Latvian, English otherwise.",
      section: "Getting around",
      i18n: { lv: { title: "Lejupielādēt lietotāja rokasgrāmatu", body: "Lejupielādē pilnu QTG lietotāja rokasgrāmatu PDF formātā ar katras lapas ekrānuzņēmumiem. To saņemat iestatījumos izvēlētajā valodā: latviski, ja lietotne ir latviešu valodā, citādi angliski." } },
    },

    // --- Start an offer -------------------------------------------------------
    {
      selector: '[data-help="offers-new-menu"]',
      title: "Start a new offer",
      body: "Opens a menu with the two products. Choose MTPL for compulsory motor third-party liability or CASCO for own-damage cover; the offer page opens empty on its General Information tab.",
      section: "Start an offer",
      i18n: { lv: { title: "Sākt jaunu piedāvājumu", body: "Atver izvēlni ar abiem produktiem. Izvēlieties OCTA obligātajai transportlīdzekļu īpašnieku civiltiesiskajai apdrošināšanai vai KASKO transportlīdzekļa bojājumu segumam; piedāvājuma lapa atveras tukša cilnē “Vispārīgā informācija”." } },
    },
    {
      selector: '[data-help="offers-new-mtpl"]',
      title: "New MTPL offer",
      body: "Starts an MTPL offer. You fill in the vehicle and client, then choose insurers and periods (1–12 months) and get one premium per insurer and period.",
      section: "Start an offer",
      context: "new-menu",
      i18n: { lv: { title: "Jauns OCTA piedāvājums", body: "Sāk OCTA piedāvājumu. Aizpildiet transportlīdzekli un klientu, pēc tam izvēlieties apdrošinātājus un periodus (1–12 mēneši) un saņemiet vienu prēmiju katram apdrošinātājam un periodam." } },
    },
    {
      selector: '[data-help="offers-new-casco"]',
      title: "New CASCO offer",
      body: "Starts a CASCO offer. Besides the vehicle and client it asks for the vehicle value, the other driver and the coverage territory, then compares insurers' variants and payment plans.",
      section: "Start an offer",
      context: "new-menu",
      i18n: { lv: { title: "Jauns KASKO piedāvājums", body: "Sāk KASKO piedāvājumu. Papildus transportlīdzeklim un klientam tas prasa transportlīdzekļa vērtību, cita vadītāja datus un darbības teritoriju, pēc tam salīdzina apdrošinātāju variantus un maksājumu plānus." } },
    },

    // --- Find an offer ---------------------------------------------------------
    {
      selector: '[data-help="offers-search"]',
      title: "Search the offers",
      body: "Type what you know about the offer — for example a client name, client code or licence plate. In a word with characters such as \"-\" or \"/\", type each part between them in full: \"010101-12345\" finds 010101-12345, \"010101-123\" does not. The list reloads half a second after you stop typing and keeps the product filter and column filters.",
      section: "Find an offer",
      i18n: { lv: { title: "Meklēt piedāvājumus", body: "Ierakstiet to, ko zināt par piedāvājumu, — piemēram, klienta nosaukumu, klienta kodu vai numura zīmi. Vārdā ar tādām rakstzīmēm kā “-” vai “/” katru daļu starp tām ierakstiet pilnībā: “010101-12345” atrod 010101-12345, bet “010101-123” – ne. Saraksts pārlādējas pussekundi pēc tam, kad beidzat rakstīt, un saglabā produkta un kolonnu filtrus." } },
    },
    {
      selector: '[data-help="offers-reload"]',
      title: "Reload the list",
      body: "Fetches the list again from the top — use it after a colleague changed an offer, or when a status you expect has not appeared yet.",
      section: "Find an offer",
      i18n: { lv: { title: "Pārlādēt sarakstu", body: "Vēlreiz ielādē sarakstu no sākuma — izmantojiet to, ja kolēģis ir mainījis piedāvājumu vai gaidītais statuss vēl nav parādījies." } },
    },
    {
      selector: '[data-help="offers-product-filter"]',
      title: "Show MTPL, CASCO or both",
      body: "Limits the list to offers that contain an MTPL or a CASCO quote. An offer with both products appears under either choice.",
      section: "Find an offer",
      i18n: { lv: { title: "Rādīt OCTA, KASKO vai abus", body: "Ierobežo sarakstu ar piedāvājumiem, kuros ir OCTA vai KASKO kotācija. Piedāvājums ar abiem produktiem parādās pie jebkuras izvēles." } },
    },
    {
      selector: '[data-help="offers-column-options"]',
      title: "Choose columns and filter them",
      body: 'Opens a window: pick columns from the grouped, searchable list on the left, then reorder, filter or remove them in the "Selected" list on the right; only selected columns can filter. Nothing changes until you press "Apply" and "Close" discards your changes. Save the layout as a named view; an "Unsaved changes" tag shows when it differs from the selected view, until you press "Save current view". The icon changes while a filter is active.',
      section: "Find an offer",
      i18n: { lv: { title: "Izvēlēties un filtrēt kolonnas", body: "Atver logu: kreisajā pusē izvēlieties kolonnas no grupēta saraksta ar meklēšanu, bet labajā pusē sarakstā “Atlasītās” tās pārkārtojiet, filtrējiet vai noņemiet; filtrēt var tikai pēc atlasītajām kolonnām. Nekas nemainās, kamēr nenospiežat “Lietot”, un “Aizvērt” izmaiņas atmet. Saglabājiet izkārtojumu kā nosauktu skatu; zīme “Nesaglabātas izmaiņas” rāda, ka tas atšķiras no izvēlētā skata, līdz nospiežat “Saglabāt pašreizējo skatu”. Kamēr filtrs ir aktīvs, ikona mainās." } },
    },
    {
      selector: '[data-help="offers-result-count"]',
      title: "How many offers match",
      body: "The number of offers that match the search and all filters — not only the rows loaded on screen so far.",
      section: "Find an offer",
      i18n: { lv: { title: "Cik piedāvājumu atbilst", body: "Piedāvājumu skaits, kas atbilst meklēšanai un visiem filtriem, — ne tikai līdz šim ekrānā ielādētās rindas." } },
    },
    {
      selector: '[data-help="offers-casco-conditions"]',
      title: "Open the CASCO conditions",
      body: "Opens the settings page with each insurer's CASCO conditions used in quotes and documents. Only administrators and read-only administrators see this button; only administrators can change the conditions.",
      section: "Find an offer",
      requires: "canViewSettings",
      i18n: { lv: { title: "Atvērt KASKO nosacījumus", body: "Atver iestatījumu lapu ar katra apdrošinātāja KASKO nosacījumiem, ko izmanto kotācijās un dokumentos. Šo pogu redz tikai administratori un tikai lasīšanas administratori; mainīt nosacījumus var tikai administratori." } },
    },

    {
      selector: '[data-help="offers-show-mode"]',
      title: "Show valid, error or all entries",
      body: 'Only for administrators and read-only administrators. "Valid entries" (the default) hides offers marked as error, "Error entries" lists only them and "All entries" lists both. Everybody else always sees the valid entries; search, filters and exports follow this choice. Only administrators can mark or unmark an offer.',
      section: "Find an offer",
      requires: "canChooseOffersShowMode",
      i18n: { lv: { title: "Rādīt derīgos, kļūdainos vai visus ierakstus", body: "Tikai administratoriem un tikai lasīšanas administratoriem. Atzīmēt piedāvājumu vai noņemt atzīmi var tikai administratori. “Derīgie ieraksti” (noklusējums) paslēpj piedāvājumus, kas atzīmēti kā kļūdaini, “Kļūdainie ieraksti” rāda tikai tos, bet “Visi ieraksti” — abus. Visi pārējie lietotāji vienmēr redz derīgos ierakstus; meklēšana, filtri un eksports ievēro šo izvēli." } },
    },

    // --- Read the list ---------------------------------------------------------
    {
      selector: '[data-help="offers-table"]',
      title: "Read and sort the list",
      body: "One row per offer. Click a column header to sort ascending, again for descending, a third time to remove the sort. Scroll down to load the next 40 rows. Tick the checkbox at the start of a row to select it for the actions at the bottom of the page.",
      section: "Read the list",
      i18n: { lv: { title: "Lasīt un kārtot sarakstu", body: "Viena rinda katram piedāvājumam. Noklikšķiniet uz kolonnas virsraksta, lai kārtotu augošā secībā, vēlreiz — dilstošā, trešo reizi — lai kārtošanu noņemtu. Ritiniet uz leju, lai ielādētu nākamās 40 rindas. Atzīmējiet izvēles rūtiņu rindas sākumā, lai to atlasītu darbībām lapas apakšā." } },
    },
    {
      selector: '[data-help="offers-offer-id-link"]',
      title: "Open an offer",
      body: "Click the Offer ID to open the offer with its quotes, documents and invoice. Clicking elsewhere in the row only selects it.",
      section: "Read the list",
      optional: true,
      i18n: { lv: { title: "Atvērt piedāvājumu", body: "Noklikšķiniet uz piedāvājuma ID, lai atvērtu piedāvājumu ar tā kotācijām, dokumentiem un rēķinu. Klikšķis citur rindā to tikai atlasa." } },
    },
    {
      selector: '[data-help="offers-product-icons"]',
      title: "See the offer's products",
      body: "The blue car means the offer has CASCO quotes, the orange shield means MTPL quotes; both icons mean both products. Hover for the name.",
      section: "Read the list",
      optional: true,
      i18n: { lv: { title: "Redzēt piedāvājuma produktus", body: "Zilā automašīna nozīmē, ka piedāvājumā ir KASKO kotācijas, oranžais vairogs — OCTA kotācijas; abas ikonas nozīmē abus produktus. Novietojiet peli virs ikonas, lai redzētu nosaukumu." } },
    },
    {
      selector: '[data-help="offers-client-type-icon"]',
      title: "Company or private client",
      body: "A briefcase marks a company client, a person icon a private individual.",
      section: "Read the list",
      optional: true,
      i18n: { lv: { title: "Uzņēmums vai privātpersona", body: "Portfelis apzīmē klientu — uzņēmumu, cilvēka ikona — fizisku personu." } },
    },

    // --- Act on one offer ------------------------------------------------------
    {
      selector: '[data-help="offers-row-menu"]',
      title: "Open the offer's actions",
      body: "The ⋮ menu on each row: duplicate the offer, mark its invoice as paid or as issued, or invalidate it; administrators can also mark it as error. Only the actions that fit the offer's status are shown or enabled.",
      section: "Act on one offer",
      optional: true,
      i18n: { lv: { title: "Atvērt piedāvājuma darbības", body: "⋮ izvēlne katrā rindā: dublēt piedāvājumu, atzīmēt tā rēķinu kā apmaksātu vai izsniegtu, vai atcelt piedāvājumu; administratori to var arī atzīmēt kā kļūdainu. Redzamas vai pieejamas ir tikai piedāvājuma statusam atbilstošās darbības." } },
    },
    {
      selector: '[data-help="offers-row-menu-duplicate"]',
      title: "Duplicate an offer",
      body: "Opens a new offer pre-filled with this offer's vehicle, client and insurance data, without its number, status or quotes. Use it for a renewal or a similar vehicle; nothing is saved until you create the new offer.",
      section: "Act on one offer",
      context: "row-menu",
      i18n: { lv: { title: "Dublēt piedāvājumu", body: "Atver jaunu piedāvājumu, kas aizpildīts ar šī piedāvājuma transportlīdzekļa, klienta un apdrošināšanas datiem, bet bez numura, statusa un kotācijām. Izmantojiet to atjaunošanai vai līdzīgam transportlīdzeklim; nekas netiek saglabāts, kamēr neizveidojat jauno piedāvājumu." } },
    },
    {
      selector: '[data-help="offers-row-menu-mark-paid"]',
      title: "Mark the invoice as paid",
      body: "Shown for offers in status Invoice, Invoice Sent, Group Invoice or Group Invoice Sent, enabled while the offer's own invoice is still unpaid. Only this offer is marked: the status becomes Invoice Paid, also for an offer in a group invoice, which then stays open for its other offers. This cannot be undone.",
      section: "Act on one offer",
      context: "row-menu",
      optional: true,
      i18n: { lv: { title: "Atzīmēt rēķinu kā apmaksātu", body: "Redzams piedāvājumiem statusā “Rēķins”, “Rēķins nosūtīts”, “Grupas rēķins” vai “Grupas rēķins nosūtīts”, un pieejams, kamēr paša piedāvājuma rēķins vēl nav apmaksāts. Tiek atzīmēts tikai šis piedāvājums: statuss kļūst “Rēķins apmaksāts”, arī grupas rēķinā iekļautam piedāvājumam, un grupas rēķins pārējiem piedāvājumiem paliek atvērts. To nevar atsaukt." } },
    },
    {
      selector: '[data-help="offers-row-menu-mark-group-paid"]',
      title: "Mark the group invoice as paid",
      body: "Shown for offers in status Group Invoice or Group Invoice Sent, enabled while the group invoice is unpaid. After you confirm, every offer of the group invoice that is not paid yet becomes Group Invoice Paid; offers already marked as paid on their own keep the status Invoice Paid. This cannot be undone.",
      section: "Act on one offer",
      context: "row-menu",
      optional: true,
      i18n: { lv: { title: "Atzīmēt grupas rēķinu kā apmaksātu", body: "Redzams piedāvājumiem statusā “Grupas rēķins” vai “Grupas rēķins nosūtīts”, un pieejams, kamēr grupas rēķins nav apmaksāts. Pēc apstiprināšanas visi vēl neapmaksātie grupas rēķina piedāvājumi kļūst “Grupas rēķins apmaksāts”; piedāvājumi, kas jau atsevišķi atzīmēti kā apmaksāti, saglabā statusu “Rēķins apmaksāts”. To nevar atsaukt." } },
    },
    {
      selector: '[data-help="offers-row-menu-mark-issued"]',
      title: "Mark a paid invoice as issued",
      body: "Shown only for paid invoices. Confirms that the insurer issued the policy: the status becomes Policy Issued.",
      section: "Act on one offer",
      context: "row-menu",
      optional: true,
      i18n: { lv: { title: "Atzīmēt apmaksāto rēķinu kā izsniegtu", body: "Redzams tikai apmaksātiem rēķiniem. Apstiprina, ka apdrošinātājs ir izsniedzis polisi: statuss kļūst “Polise izsniegta”." } },
    },
    {
      selector: '[data-help="offers-row-menu-invalidate"]',
      title: "Invalidate an offer",
      body: "Cancels an offer that will not be taken up. Only possible while no insurer has been chosen and the invoice (if any) has not been sent or paid. The offer disappears from the list and cannot be restored.",
      section: "Act on one offer",
      context: "row-menu",
      i18n: { lv: { title: "Atcelt piedāvājumu", body: "Atceļ piedāvājumu, kas netiks izmantots. Iespējams tikai tad, ja vēl nav izvēlēts apdrošinātājs un rēķins (ja tāds ir) nav nosūtīts vai apmaksāts. Piedāvājums pazūd no saraksta, un to nevar atjaunot." } },
    },
    {
      selector: '[data-help="offers-invalidate-confirm"]',
      title: "Confirm the invalidation",
      body: 'Invalidates the offer and reloads the list. Press "Cancel" to keep it.',
      section: "Act on one offer",
      context: "invalidate-dialog",
      i18n: { lv: { title: "Apstiprināt atcelšanu", body: "Atceļ piedāvājumu un pārlādē sarakstu. Nospiediet “Atcelt” dialoglodziņā, ja piedāvājumu vēlaties paturēt." } },
    },
    {
      selector: '[data-help="offers-row-menu-mark-as-error"]',
      title: "Mark an offer as error",
      body: "Only for administrators, in any status. The offer is shaded red, disappears from the valid entries and allows no other action — it can still be opened and viewed, but not changed, invoiced, paid or sent — until it is unmarked.",
      section: "Act on one offer",
      context: "row-menu",
      optional: true,
      requires: "canMarkOffersAsError",
      i18n: { lv: { title: "Atzīmēt piedāvājumu kā kļūdainu", body: "Tikai administratoriem, jebkurā statusā. Piedāvājums tiek iekrāsots sarkans, pazūd no derīgajiem ierakstiem un neļauj veikt nevienu citu darbību — to joprojām var atvērt un apskatīt, bet nevar mainīt, izrakstīt rēķinu, apmaksāt vai nosūtīt —, kamēr atzīme netiek noņemta." } },
    },
    {
      selector: '[data-help="offers-mark-as-error-confirm"]',
      title: "Confirm marking or unmarking",
      body: 'Marks the offer as error (or removes the mark) and reloads the list. Press "Cancel" to leave it as it is.',
      section: "Act on one offer",
      context: "mark-as-error-dialog",
      requires: "canMarkOffersAsError",
      i18n: { lv: { title: "Apstiprināt atzīmēšanu vai atzīmes noņemšanu", body: "Atzīmē piedāvājumu kā kļūdainu (vai noņem atzīmi) un pārlādē sarakstu. Nospiediet “Atcelt”, lai to atstātu kā ir." } },
    },
    {
      selector: '[data-help="offers-row-menu-unmark-as-error"]',
      title: "Unmark an error entry",
      body: "The only action on an offer marked as error. It clears the mark with its date and user, and the offer is a valid entry again with all its usual actions.",
      section: "Act on one offer",
      context: "error-entries",
      optional: true,
      requires: "canMarkOffersAsError",
      i18n: { lv: { title: "Noņemt kļūdas atzīmi", body: "Vienīgā darbība ar piedāvājumu, kas atzīmēts kā kļūdains. Tā notīra atzīmi ar tās datumu un lietotāju, un piedāvājums atkal ir derīgs ieraksts ar visām ierastajām darbībām." } },
    },

    // --- Act on several offers -------------------------------------------------
    {
      selector: '[data-help="offers-selection-count"]',
      title: "Selected offers",
      body: "The bar at the bottom appears as soon as a row is ticked and counts the selected offers. Selection survives scrolling and reloading; sorting clears it.",
      section: "Act on several offers",
      context: "selection",
      i18n: { lv: { title: "Atlasītie piedāvājumi", body: "Josla apakšā parādās, tiklīdz atzīmēta kāda rinda, un rāda atlasīto piedāvājumu skaitu. Atlase saglabājas, ritinot un pārlādējot; kārtošana to notīra." } },
    },
    {
      selector: '[data-help="offers-check-payments"]',
      title: "Check payments against the bank",
      body: "Looks up bank statement entries that may pay the selected unpaid invoices. Offers whose amount matches exactly are pre-ticked; review the matches and mark the invoices as paid in one go. Disabled when no selected offer has an unpaid invoice.",
      section: "Act on several offers",
      context: "selection",
      i18n: { lv: { title: "Pārbaudīt maksājumus bankā", body: "Atrod bankas izraksta ierakstus, ar kuriem varētu būt apmaksāti atlasītie neapmaksātie rēķini. Piedāvājumi, kuru summa precīzi sakrīt, ir atzīmēti jau iepriekš; pārskatiet atbilstības un atzīmējiet rēķinus kā apmaksātus vienā reizē. Nav pieejams, ja nevienam atlasītajam piedāvājumam nav neapmaksāta rēķina." } },
    },
    {
      selector: '[data-help="offers-check-policies"]',
      title: "Check policies in POL",
      body: "For selected offers with a paid invoice and a VIN, finds the policies the insurers issued and compares client, insurer, premium and validity. Offers that match on every point are pre-ticked; confirming marks them Policy Issued.",
      section: "Act on several offers",
      context: "selection",
      i18n: { lv: { title: "Pārbaudīt polises POL", body: "Atlasītajiem piedāvājumiem ar apmaksātu rēķinu un VIN atrod apdrošinātāju izsniegtās polises un salīdzina klientu, apdrošinātāju, prēmiju un derīgumu. Piedāvājumi, kas sakrīt visos punktos, ir atzīmēti jau iepriekš; apstiprinot tie tiek atzīmēti kā “Polise izsniegta”." } },
    },
    {
      selector: '[data-help="offers-create-group-invoice"]',
      title: "Combine invoices into a group invoice",
      body: "Enabled when at least two offers are selected and every one of them is in status Invoice (or already in a group invoice) with an invoice number. Opens the group invoice form after a confirmation.",
      section: "Act on several offers",
      context: "selection",
      i18n: { lv: { title: "Apvienot rēķinus grupas rēķinā", body: "Pieejams, ja atlasīti vismaz divi piedāvājumi un katrs no tiem ir statusā “Rēķins” (vai jau ir grupas rēķinā) ar rēķina numuru. Pēc apstiprinājuma atver grupas rēķina formu." } },
    },
    {
      selector: '[data-help="offers-group-invoice-payer"]',
      title: "Choose who pays the group invoice",
      body: "Required. Pick one of the clients or payers of the selected offers, or a different payer found in GOS or entered by hand. The payer needs an e-mail address.",
      section: "Act on several offers",
      context: "group-invoice",
      i18n: { lv: { title: "Izvēlēties grupas rēķina maksātāju", body: "Obligāti. Izvēlieties kādu no atlasīto piedāvājumu klientiem vai maksātājiem vai citu maksātāju, kas atrasts GOS vai ievadīts manuāli. Maksātājam vajadzīga e-pasta adrese." } },
    },
    {
      selector: '[data-help="offers-group-invoice-due-date"]',
      title: "Group invoice due date",
      body: "Defaults to the latest due date of the selected invoices. It cannot be in the past.",
      section: "Act on several offers",
      context: "group-invoice",
      i18n: { lv: { title: "Grupas rēķina apmaksas termiņš", body: "Pēc noklusējuma — vēlākais no atlasīto rēķinu apmaksas termiņiem. Tas nevar būt pagātnē." } },
    },
    {
      selector: '[data-help="offers-group-invoice-create"]',
      title: "Create the group invoice",
      body: "Creates one invoice for all listed lines; every included offer moves to status Group Invoice and the selection is cleared.",
      section: "Act on several offers",
      context: "group-invoice",
      i18n: { lv: { title: "Izveidot grupas rēķinu", body: "Izveido vienu rēķinu visām uzskaitītajām rindām; katrs iekļautais piedāvājums pāriet statusā “Grupas rēķins”, un atlase tiek notīrīta." } },
    },
    {
      selector: '[data-help="offers-clear-selection"]',
      title: "Clear the selection",
      body: "Unticks every selected offer and hides the bar.",
      section: "Act on several offers",
      context: "selection",
      i18n: { lv: { title: "Notīrīt atlasi", body: "Noņem atzīmi visiem atlasītajiem piedāvājumiem un paslēpj joslu apakšā." } },
    },

    // --- Export and bulk import --------------------------------------------------
    {
      selector: '[data-help="offers-export-excel"]',
      title: "Export to Excel",
      body: "Exports every offer requested in a period you choose that matches the current search, filters and sort — not only the rows on screen — with the columns currently visible. Invalidated offers and offers marked as error are shaded.",
      section: "Export and bulk import",
      i18n: { lv: { title: "Eksportēt uz Excel", body: "Eksportē visus izvēlētajā periodā pieprasītos piedāvājumus, kas atbilst pašreizējai meklēšanai, filtriem un kārtošanai, — ne tikai ekrānā redzamās rindas — ar pašlaik redzamajām kolonnām. Anulētie un kā kļūdaini atzīmētie piedāvājumi ir iekrāsoti." } },
    },
    {
      selector: '[data-help="offers-export-pdf"]',
      title: "Export to PDF",
      body: "The same selection as the Excel export, as a PDF that opens in a new tab.",
      section: "Export and bulk import",
      i18n: { lv: { title: "Eksportēt uz PDF", body: "Tā pati atlase kā Excel eksportā, bet kā PDF, kas atveras jaunā cilnē." } },
    },
    {
      selector: '[data-help="offers-export-from"]',
      title: "Export period — from",
      body: "First day of the period, by the date the offer was requested. The last two months are suggested.",
      section: "Export and bulk import",
      context: "export-dialog",
      i18n: { lv: { title: "Eksporta periods — no", body: "Perioda pirmā diena pēc piedāvājuma pieprasīšanas datuma. Tiek ieteikti pēdējie divi mēneši." } },
    },
    {
      selector: '[data-help="offers-export-to"]',
      title: "Export period — to",
      body: "Last day of the period. It must not be before the start date.",
      section: "Export and bulk import",
      context: "export-dialog",
      i18n: { lv: { title: "Eksporta periods — līdz", body: "Perioda pēdējā diena. Tā nedrīkst būt pirms sākuma datuma." } },
    },
    {
      selector: '[data-help="offers-export-confirm"]',
      title: "Run the export",
      body: "Builds the file. If no offer matches, you get a message instead of an empty file.",
      section: "Export and bulk import",
      context: "export-dialog",
      i18n: { lv: { title: "Palaist eksportu", body: "Izveido failu. Ja neviens piedāvājums neatbilst, tukša faila vietā saņemat paziņojumu." } },
    },
    {
      selector: '[data-help="offers-export-invoices"]',
      title: "Export invoices for premium collection",
      body: "Downloads an Excel file with every MTPL, CASCO and group invoice issued in a period you choose (the last seven days are suggested) — number, issue, due and paid dates, payer, amount to pay (premium minus discount), and per member the VIN, insurance line, insurer, client and offer id — for import into the Premium Collection Tool. The line invoices of grouped offers are covered by their group invoice and are not listed separately.",
      section: "Export and bulk import",
      i18n: { lv: { title: "Eksportēt rēķinus prēmiju iekasēšanai", body: "Lejupielādē Excel failu ar visiem OCTA, KASKO un grupas rēķiniem, kas izrakstīti jūsu izvēlētajā periodā (tiek ieteiktas pēdējās septiņas dienas), — numurs, izrakstīšanas, apmaksas termiņa un samaksas datumi, maksātājs, maksājamā summa (prēmija mīnus atlaide) un par katru dalībnieku VIN, apdrošināšanas veids, apdrošinātājs, klients un piedāvājuma ID — importēšanai prēmiju iekasēšanas rīkā (PCT). Grupētu piedāvājumu atsevišķos rēķinus aptver to grupas rēķins, un tie netiek uzskaitīti atsevišķi." } },
    },
    {
      selector: '[data-help="offers-export-invoices-from"]',
      title: "Issued from",
      body: "First day of the period. Seven days ago is suggested; a future date is refused.",
      section: "Export and bulk import",
      context: "export-invoices-dialog",
      i18n: { lv: { title: "Izrakstīts no", body: "Perioda pirmā diena. Tiek ieteikta diena pirms septiņām dienām; nākotnes datums netiek pieņemts." } },
    },
    {
      selector: '[data-help="offers-export-invoices-to"]',
      title: "Issued to",
      body: "Last day of the period, included. Today is suggested. It must not be before the start date, and the period may be at most one month long.",
      section: "Export and bulk import",
      context: "export-invoices-dialog",
      i18n: { lv: { title: "Izrakstīts līdz", body: "Perioda pēdējā diena, ieskaitot. Tiek ieteikta šodiena. Tā nedrīkst būt pirms sākuma datuma, un periods drīkst būt ne garāks par vienu mēnesi." } },
    },
    {
      selector: '[data-help="offers-export-invoices-confirm"]',
      title: "Run the invoice export",
      body: "Builds and downloads the file. If no invoice was issued in the period, you get a message instead of an empty file.",
      section: "Export and bulk import",
      context: "export-invoices-dialog",
      i18n: { lv: { title: "Palaist rēķinu eksportu", body: "Izveido un lejupielādē failu. Ja periodā nav izrakstītu rēķinu, tukša faila vietā saņemat paziņojumu." } },
    },
    {
      selector: '[data-help="offers-bulk-prefill"]',
      title: "Prefill many vehicles from Excel",
      body: "Opens the bulk prefill: upload an Excel file with vehicle attributes and the vehicle data is looked up for every row at once, optionally creating MTPL offers.",
      section: "Export and bulk import",
      i18n: { lv: { title: "Aizpildīt daudzus transportlīdzekļus no Excel", body: "Atver masveida aizpildīšanu: augšupielādējiet Excel failu ar transportlīdzekļu atribūtiem, un transportlīdzekļa dati tiek uzmeklēti visām rindām vienlaikus, pēc izvēles izveidojot arī OCTA piedāvājumus." } },
    },
    {
      selector: '[data-help="offers-bulk-prefill-dropzone"]',
      title: "Add the Excel file",
      body: "Drop one .xlsx or .xls file here or click to browse. Other file types are refused.",
      section: "Export and bulk import",
      context: "bulk-prefill",
      i18n: { lv: { title: "Pievienot Excel failu", body: "Ievelciet šeit vienu .xlsx vai .xls failu vai noklikšķiniet, lai to izvēlētos. Citi failu tipi netiek pieņemti." } },
    },
    {
      selector: '[data-help="offers-bulk-prefill-create-mtpl"]',
      title: "Also create MTPL offers",
      body: "When ticked, an MTPL offer is created for each vehicle in the file as well. Leave it off to get only the prefilled data back.",
      section: "Export and bulk import",
      context: "bulk-prefill",
      i18n: { lv: { title: "Izveidot arī OCTA piedāvājumus", body: "Ja atzīmēts, katram faila transportlīdzeklim tiek izveidots arī OCTA piedāvājums. Atstājiet neatzīmētu, lai saņemtu tikai aizpildītos datus." } },
    },
    {
      selector: '[data-help="offers-bulk-prefill-upload"]',
      title: "Upload and process",
      body: "Sends the file for processing. Large files can take a while: you may close the dialog, and the result file is e-mailed to you when it is ready.",
      section: "Export and bulk import",
      context: "bulk-prefill",
      i18n: { lv: { title: "Augšupielādēt un apstrādāt", body: "Nosūta failu apstrādei. Lieli faili var aizņemt laiku: dialoglodziņu var aizvērt, un rezultāta fails tiks nosūtīts jums pa e-pastu, kad būs gatavs." } },
    },
  ],
  i18n: {
    lv: {
      title: "Piedāvājumi",
      tips: [
        "Statusa kolonna ir piedāvājuma dzīves cikls: Piedāvājums → Piedāvājums saglabāts / nosūtīts → Rēķins → Rēķins nosūtīts → Rēķins apmaksāts → Polise izsniegta. Grupētiem rēķiniem ir savi grupas rēķina statusi.",
        "Klikšķis uz rindas to tikai atlasa. Lai atvērtu piedāvājumu, noklikšķiniet uz tā ID.",
        "Meklēšana un OCTA / KASKO filtrs darbojas kopā ar filtriem sadaļā “Kolonnas opcijas”; rezultātu skaits labajā pusē vienmēr rāda, cik piedāvājumu atbilst.",
      ],
      intro:
        "Visi OCTA un KASKO piedāvājumi, jaunākie augšā. Šeit sāciet jaunu piedāvājumu, atrodiet esošu, sekojiet tā statusam no piedāvājuma līdz rēķinam un izsniegtai polisei un apstrādājiet vairākus piedāvājumus vienlaikus (maksājumi, polises, grupas rēķini). Rindas ielādējas pa 40, ritinot lejup.",
      sections: {
        "Getting around": "Orientēšanās",
        "Start an offer": "Sākt piedāvājumu",
        "Find an offer": "Atrast piedāvājumu",
        "Read the list": "Saraksta lasīšana",
        "Act on one offer": "Darbības ar vienu piedāvājumu",
        "Act on several offers": "Darbības ar vairākiem piedāvājumiem",
        "Export and bulk import": "Eksports un masveida imports",
      },
      contexts: {
        "new-menu": "Noklikšķiniet uz \"Jauns piedāvājums\" augšā pa kreisi.",
        "row-menu": "Noklikšķiniet uz ⋮ blakus piedāvājuma ID.",
        "selection": "Atzīmējiet vismaz vienas rindas izvēles rūtiņu.",
        "export-dialog": "Noklikšķiniet uz \"Eksportēt Excel\" vai \"Eksportēt PDF\".",
        "export-invoices-dialog": "Noklikšķiniet uz rēķinu eksporta ikonas augšējā labajā stūrī.",
        "group-invoice": "Atlasiet divas vai vairāk rindas statusā Rēķins, noklikšķiniet uz \"Izveidot grupas rēķinu\" un pēc tam uz \"Turpināt\".",
        "invalidate-dialog": "Atveriet ⋮ piedāvājumam bez izvēlēta apdrošinātāja un noklikšķiniet uz \"Atcelt piedāvājumu\".",
        "mark-as-error-dialog": "Atveriet ⋮ piedāvājumam un noklikšķiniet uz \"Atzīmēt kā kļūdu\" (tikai administratoriem).",
        "error-entries": "Nolaižamajā sarakstā blakus meklēšanas laukam izvēlieties \"Kļūdainie ieraksti\" vai \"Visi ieraksti\" (administratoriem un tikai lasīšanas administratoriem).",
        "bulk-prefill": "Noklikšķiniet uz \"Masveida aizpildīšana\".",
      },
    },
  },
};
